'use client';

import { useTranslations } from 'next-intl';
import PlayerAvatar from '@/components/player/PlayerAvatar';
import { Card } from '@/components/ui/card';
import type { Tables } from '@/types/supabase';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate } from '@/lib/utils';

interface ProfileHeroCardProps {
  player: Tables<'player'>;
  locale: string;
  editButton?: React.ReactNode;
}

export default function ProfileHeroCard({ player, locale, editButton }: ProfileHeroCardProps) {
  const t = useTranslations('Profile');
  const formatName = useFormatName();

  const playerName = formatName(player.first_name, player.last_name);
  const gender = player.gender ? t(player.gender) : null;

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center gap-4 sm:gap-6">
        {/* 64px on phones, 96px from `sm` — the row layout needs the width for the name. */}
        <PlayerAvatar player={player} size="lg" className="sm:h-24 sm:w-24 sm:text-2xl" />
        <div className="min-w-0 flex-1 space-y-1 sm:space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-foreground text-xl font-bold sm:text-2xl">{playerName}</h1>
              <p className="text-muted">@{player.username}</p>
            </div>
            {editButton}
          </div>
          <div className="text-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {player.created_at && (
              <span>
                {t('joined')}{' '}
                {/* `created_at` is a timestamptz. <LocalDateTime> covers BOTH #418
                    axes: UTC until mount (timezone), and suppressHydrationWarning
                    for Node-vs-browser ICU divergence. Never call formatDate
                    directly in a client render. */}
                <LocalDateTime iso={player.created_at} locale={locale} format={formatDate} />
              </span>
            )}
            {gender && <span>{gender}</span>}
          </div>
        </div>
      </div>
    </Card>
  );
}
